'use client';

import { useEffect, useState, useCallback, useMemo, useRef, Suspense } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { useStore } from '@/lib/store';
import { tmdb } from '@/lib/tmdb';
import { ContentChecker, ContentUpdate } from '@/lib/contentChecker';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Search,
  Filter,
  Plus,
  TrendingUp,
  Clock,
  Star,
  Tv,
  Film,
  Bell,
  LogOut,
  Sparkles,
  Calendar,
  ArrowRight,
  PlayCircle,
  Globe,
  RefreshCw,
  ArrowUpDown,
  X
} from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { SearchDialog } from '@/components/SearchDialog';
import { WatchlistCard } from '@/components/WatchlistCard';
import { Header } from '@/components/Header';
import { NotificationPermissionPrompt } from '@/components/NotificationPermissionPrompt';
import { HierarchicalFilter } from '@/components/HierarchicalFilter';
import { FilterSortDialog } from '@/components/FilterSortDialog';
import { PlaylistsPanel } from '@/components/PlaylistsPanel';
import { WatchTogetherPanel } from '@/components/WatchTogetherPanel';
import { ItemDetailDialog } from '@/components/ItemDetailDialog';
import { CollapsibleSection } from '@/components/CollapsibleSection';
import { useItemDetailDialog } from '@/hooks/useItemDetailDialog';

export default function DashboardPage() {
  return (
    <Suspense fallback={<DashboardSkeleton />}>
      <DashboardContent />
    </Suspense>
  );
}

function DashboardSkeleton() {
  return (
    <div className="min-h-screen bg-black">
      <div className="px-gutter pt-20 md:pt-24 pb-8">
        <Skeleton className="h-10 w-48 mb-8 rounded-xl" />
        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 2xl:grid-cols-8 gap-x-3 md:gap-x-4 gap-y-6 md:gap-y-8">
          {Array.from({ length: 12 }).map((_, i) => (
            <Skeleton key={i} className="aspect-[2/3] rounded-xl" />
          ))}
        </div>
      </div>
    </div>
  );
}

function DashboardContent() {
  const { user, userProfile, loading: authLoading, logout } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [highlightedItemId, setHighlightedItemId] = useState<number | null>(null);
  // Hub tab: watchlist sections, shareable playlists, or Watch Together links
  type DashboardTab = 'watchlist' | 'playlists' | 'together';
  const tabParam = searchParams.get('tab');
  const tabFromUrl: DashboardTab = tabParam === 'playlists' || tabParam === 'together' ? tabParam : 'watchlist';
  const [dashboardTab, setDashboardTab] = useState<DashboardTab>(tabFromUrl);

  // Follow ?tab= when it changes while already on the dashboard (e.g. the
  // "View your linked shows" button or a notification deep link)
  useEffect(() => {
    setDashboardTab(tabFromUrl);
  }, [tabFromUrl]);

  // Keep the tab in the URL so refresh / back / sharing land on the same tab
  const selectDashboardTab = (tab: DashboardTab) => {
    setDashboardTab(tab);
    const params = new URLSearchParams(searchParams.toString());
    if (tab === 'watchlist') params.delete('tab');
    else params.set('tab', tab);
    params.delete('highlight');
    const qs = params.toString();
    router.replace(qs ? `/dashboard?${qs}` : '/dashboard', { scroll: false });
  };
  const {
    watchlist,
    watchlistSynced,
    filterType,
    sortBy,
    searchQuery,
    statusFilter,
    genreFilter,
    activeFilters,
    setFilterType,
    setSortBy,
    setSearchQuery,
    setStatusFilter,
    setGenreFilter,
    updateWatchlistItem,
    clearNewlyAddedStatus,
    clearAllFilters,
  } = useStore();

  const [showSearchDialog, setShowSearchDialog] = useState(false);
  const [itemsToShow, setItemsToShow] = useState<Record<string, number>>({
    recentlyAddedTV: 20,
    newContentTV: 20,
    currentlyWatching: 20,
    inProgressTV: 20,
    returningTV: 20,
    unwatchedTV: 20,
    completeTV: 20,
    newlyAddedMovies: 20,
    unwatchedMovies: 20,
    watchedMovies: 20,
  });
  const [checkingForUpdates, setCheckingForUpdates] = useState(false);
  const [showFilterSortDialog, setShowFilterSortDialog] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<'sort' | 'filter' | null>(null);

  // Item detail dialog
  const {
    isOpen: isDetailDialogOpen,
    currentItemId,
    currentItemType,
    openDialog: openDetailDialog,
    closeDialog: closeDetailDialog,
    navigateNext,
    navigatePrev,
    canNavigateNext,
    canNavigatePrev,
  } = useItemDetailDialog();

  // Refs for intersection observers (auto-loading more items)
  const observersRef = useRef<Map<string, IntersectionObserver>>(new Map());

  // Callback refs for intersection observers
  const createObserverRef = (section: keyof typeof itemsToShow) => (element: HTMLDivElement | null) => {
    // Clean up existing observer if any
    const existingObserver = observersRef.current.get(section);
    if (existingObserver) {
      existingObserver.disconnect();
      observersRef.current.delete(section);
    }

    if (element) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setItemsToShow(prev => ({
                ...prev,
                [section]: prev[section] + 20
              }));
            }
          });
        },
        {
          root: null,
          rootMargin: '500px',
          threshold: 0,
        }
      );
      observer.observe(element);
      observersRef.current.set(section, observer);
    }
  };

  const recentlyAddedTVObserverRef = createObserverRef('recentlyAddedTV');
  const newContentTVObserverRef = createObserverRef('newContentTV');
  const currentlyWatchingObserverRef = createObserverRef('currentlyWatching');
  const inProgressTVObserverRef = createObserverRef('inProgressTV');
  const returningTVObserverRef = createObserverRef('returningTV');
  const unwatchedTVObserverRef = createObserverRef('unwatchedTV');
  const completeTVObserverRef = createObserverRef('completeTV');
  const newlyAddedMoviesObserverRef = createObserverRef('newlyAddedMovies');
  const unwatchedMoviesObserverRef = createObserverRef('unwatchedMovies');
  const watchedMoviesObserverRef = createObserverRef('watchedMovies');

  useEffect(() => {
    if (!authLoading && !user) {
      router.push('/auth/signin');
    } else if (!authLoading && user && userProfile && !userProfile.emailVerified) {
      // Redirect to verification page if email not verified
      router.push('/verify-email');
    }
  }, [user, userProfile, authLoading, router]);

  // (Trending/popular rows and personalized recommendations used to be
  // fetched here on every visit, but nothing on this page renders them —
  // removed to save ~6+ TMDB requests per dashboard load.)
  useEffect(() => {
    if (user) {
      // Clear newly added status on page load
      clearNewlyAddedStatus();
    }
  }, [user]);

  // Handle highlighting item from URL parameter
  useEffect(() => {
    const highlightParam = searchParams.get('highlight');
    if (highlightParam) {
      const itemId = parseInt(highlightParam);
      setHighlightedItemId(itemId);

      // Scroll to the item after a brief delay to ensure DOM is ready
      const scrollTimer = setTimeout(() => {
        const element = document.getElementById(`watchlist-item-${itemId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 500);

      // Remove highlight after 3 seconds
      const clearTimer = setTimeout(() => {
        setHighlightedItemId(null);
      }, 3000);

      return () => {
        clearTimeout(scrollTimer);
        clearTimeout(clearTimer);
      };
    }
  }, [searchParams]);

  // Depends on "has a watchlist" too: on a fresh device the watchlist only
  // arrives from Firestore after `user` is set, and with [user] alone the
  // check never ran there. The ref keeps it to one run per visit.
  const hasWatchlist = watchlist.length > 0;
  const contentCheckStartedRef = useRef(false);
  useEffect(() => {
    // AUTOMATIC UPDATE: Defer checking for updates to avoid blocking initial render
    if (user && hasWatchlist && !contentCheckStartedRef.current) {
      // Wait 3 seconds after page load to check for updates
      // This ensures the page is fully interactive before running expensive operations
      const timeoutId = setTimeout(() => {
        contentCheckStartedRef.current = true;
        checkForContentUpdates();
      }, 3000);

      return () => clearTimeout(timeoutId);
    }
  }, [user, hasWatchlist]);

  // Note: Removed 15-minute interval polling - notifications are now handled by server-side cron jobs

  const checkForContentUpdates = async (forceCheck = false) => {
    if (watchlist.length === 0 || checkingForUpdates) return;

    const tvShows = watchlist.filter(item => item.type === 'tv');

    if (tvShows.length === 0) {
      return;
    }

    setCheckingForUpdates(true);

    try {
      // Use intelligent scheduling - only check shows that are due
      const updates = await ContentChecker.checkForUpdates(tvShows, forceCheck);

      if (updates.length > 0) {
        let newContentCount = 0;

        updates.forEach((update: ContentUpdate) => {
          const updateData: any = {
            lastChecked: new Date().toISOString(),
            totalSeasons: update.totalSeasons,
            // NEW: Update scheduling fields
            status: update.status,
            nextCheckDate: update.nextCheckDate,
            updateFrequency: update.updateFrequency,
            // Always refresh the upcoming-episode pointer so a stale
            // past-dated nextEpisode can't linger and render "Available Now"
            nextEpisode: update.nextEpisode,
          };

          // Only flag as new content if show is not ended/cancelled
          if ((update.hasNewSeason || update.hasNewEpisodes) && update.status !== 'Ended' && update.status !== 'Cancelled') {
            newContentCount++;
            updateData.hasNewContent = true;
            updateData.newSeasonNumber = update.hasNewSeason ? update.newSeasonNumber : undefined;
          } else {
            // Clear new content flags if no longer new OR if show ended/cancelled
            updateData.hasNewContent = false;
            updateData.newSeasonNumber = undefined;
          }

          updateWatchlistItem(update.showId, updateData);
        });
      }
    } catch (error) {
    } finally {
      setCheckingForUpdates(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    router.push('/');
  };

  // Helper function to check if a TV show has any watch progress
  const hasWatchProgress = (item: any) => {
    if (item.type !== 'tv') return false;

    // Check if any seasons are marked as watched
    if (item.watchedSeasons && item.watchedSeasons.length > 0) return true;

    // Check if any episodes are marked as watched
    if (item.watchedEpisodes) {
      const totalWatchedEpisodes = Object.values(item.watchedEpisodes).reduce(
        (sum: number, episodes: any) => sum + (episodes?.length || 0),
        0
      );
      if (totalWatchedEpisodes > 0) return true;
    }

    return false;
  };

  // Helper function to determine if a show is manually marked as currently watching
  // Don't show completed shows in Watching Now
  const isWatching = (item: any) => {
    if (item.type !== 'tv' || !item.currentlyWatching) return false;

    // Don't show if completed (all seasons watched)
    if (item.totalSeasons && item.watchedSeasons.length >= item.totalSeasons) {
      return false;
    }

    return true;
  };

  // Helper to check if item is complete (all seasons watched)
  const isComplete = (item: any) => {
    if (item.type !== 'tv') return false;
    if (item.totalSeasons && item.watchedSeasons && item.watchedSeasons.length >= item.totalSeasons) {
      return true;
    }
    return false;
  };

  // Helper to check if item is recently added (within 14 days and not started)
  const isRecentlyAdded = (item: any, days: number = 14) => {
    if (!item.addedAt) return false;
    const addedDate = new Date(item.addedAt);
    const now = new Date();
    const daysSinceAdded = (now.getTime() - addedDate.getTime()) / (1000 * 60 * 60 * 24);

    // Only show in Recently Added if:
    // 1. Added within the last X days (default 14)
    // 2. Has no watch progress (not started watching)
    // 3. Not currently marked as watching
    if (item.type === 'tv') {
      return daysSinceAdded <= days && !hasWatchProgress(item) && !item.currentlyWatching;
    } else if (item.type === 'movie') {
      return daysSinceAdded <= days && !item.watched;
    }
    return false;
  };

  // Memoize filtered watchlist to prevent unnecessary recalculations
  const filteredWatchlist = useMemo(() => {
    return watchlist
      .filter((item) => {
        // NEW: Hierarchical filter logic (takes precedence over legacy filters)
        const usingHierarchicalFilters = activeFilters.length > 0;

        if (usingHierarchicalFilters) {
          let matchesFilter = false;

          // Check TV filters
          if (item.type === 'tv') {
            if (activeFilters.includes('tv-recently-added') && isRecentlyAdded(item)) matchesFilter = true;
            if (activeFilters.includes('tv-watching') && isWatching(item)) matchesFilter = true;
            if (activeFilters.includes('tv-in-progress') && hasWatchProgress(item) && !item.currentlyWatching && !isComplete(item)) matchesFilter = true;
            if (activeFilters.includes('tv-returning') && item.status === 'Returning Series') matchesFilter = true;
            if (activeFilters.includes('tv-unwatched') && !hasWatchProgress(item) && !item.currentlyWatching && !isRecentlyAdded(item)) matchesFilter = true;
            if (activeFilters.includes('tv-complete') && isComplete(item)) matchesFilter = true;
            if (activeFilters.includes('tv-new') && item.hasNewContent && item.status !== 'Ended' && item.status !== 'Cancelled') matchesFilter = true;
          }

          // Check movie filters
          if (item.type === 'movie') {
            // Same buckets as the movie sections below: "Recently Added" is the
            // newlyAdded holding bucket; every other unwatched movie is
            // "Unwatched" (a 14-day rule here hid recent movies from both)
            if (activeFilters.includes('movie-recently-added') && item.newlyAdded) matchesFilter = true;
            if (activeFilters.includes('movie-unwatched') && !item.watched && !item.newlyAdded) matchesFilter = true;
            if (activeFilters.includes('movie-complete') && item.watched) matchesFilter = true;
          }

          if (!matchesFilter) return false;
        }
        // When no hierarchical filters are active, show everything (ignore legacy filterType)

        // Search query
        if (searchQuery && !item.name.toLowerCase().includes(searchQuery.toLowerCase())) {
          return false;
        }

        // Status filter
        if (statusFilter && item.status !== statusFilter) return false;

        // Genre filter
        if (genreFilter && (!item.genres || !item.genres.includes(genreFilter))) return false;

        return true;
      })
      .sort((a, b) => {
        switch (sortBy) {
          case 'recently-added':
            return new Date(b.addedAt).getTime() - new Date(a.addedAt).getTime();
          case 'recently-updated':
            return new Date(b.lastChecked).getTime() - new Date(a.lastChecked).getTime();
          case 'name-asc':
            return a.name.localeCompare(b.name);
          case 'name-desc':
            return b.name.localeCompare(a.name);
          case 'rating-high':
            return (b.rating || 0) - (a.rating || 0);
          case 'rating-low':
            return (a.rating || 0) - (b.rating || 0);
          case 'seasons-most':
            return (b.totalSeasons || 0) - (a.totalSeasons || 0);
          case 'seasons-least':
            return (a.totalSeasons || 0) - (b.totalSeasons || 0);
          case 'personal-rank':
            return (b.personalRank || 0) - (a.personalRank || 0);
          default:
            return 0;
        }
      });
  }, [watchlist, filterType, searchQuery, statusFilter, genreFilter, sortBy, activeFilters]);

  // Memoize new content count - exclude ended/cancelled shows
  const newContentCount = useMemo(() => {
    return watchlist.filter(item =>
      item.hasNewContent &&
      item.status !== 'Ended' &&
      item.status !== 'Cancelled'
    ).length;
  }, [watchlist]);

  // Handler to open item detail dialog
  const handleOpenItemDialog = useCallback((itemId: number, itemType: 'tv' | 'movie') => {
    // Get all visible item IDs for navigation
    const visibleItemIds = filteredWatchlist.map(item => item.id);
    openDetailDialog(itemId, itemType, visibleItemIds);
  }, [filteredWatchlist, openDetailDialog]);

  // Memoize filtered category counts for better performance
  // Priority 1: Currently Watching — combined TV + Movies the user is actively engaged with.
  // Always visible regardless of active TV/movie filters; honors only the watchlist search.
  const currentlyWatchingItems = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return watchlist.filter(item => {
      if (!item.currentlyWatching) return false;
      if (item.type === 'movie' && item.watched) return false;
      if (item.type === 'tv' && isComplete(item)) return false;
      if (q && !item.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [watchlist, searchQuery]);
  const currentlyWatchingCount = currentlyWatchingItems.length;

  // Priority 2: Recently Added (exclude Watching)
  const recentlyAddedTVCount = useMemo(() => {
    return filteredWatchlist.filter(item =>
      item.type === 'tv' && isRecentlyAdded(item) && !isWatching(item)
    ).length;
  }, [filteredWatchlist]);

  // Priority 3: New Content (exclude Watching and Recently Added)
  const newContentTVCount = useMemo(() => {
    return filteredWatchlist.filter(item =>
      item.type === 'tv' &&
      item.hasNewContent &&
      item.status !== 'Ended' &&
      item.status !== 'Cancelled' &&
      !isWatching(item) &&
      !isRecentlyAdded(item)
    ).length;
  }, [filteredWatchlist]);

  // Priority 4: Returning — started shows that are coming back with more
  // episodes. Wins over In Progress; unstarted returning shows stay in
  // Unwatched instead
  const isReturningSectionItem = (item: any) =>
    item.type === 'tv' &&
    item.status === 'Returning Series' &&
    hasWatchProgress(item) && // unstarted returning shows belong to Unwatched
    !item.currentlyWatching &&
    !isWatching(item) &&
    !isRecentlyAdded(item) &&
    !(item.hasNewContent && item.status !== 'Ended' && item.status !== 'Cancelled');

  const returningTVCount = useMemo(() => {
    return filteredWatchlist.filter(isReturningSectionItem).length;
  }, [filteredWatchlist]);

  // Priority 4.5: In Progress (exclude Watching, Recently Added, New Content,
  // and Returning — started returning shows live in the Returning section)
  const inProgressTVCount = useMemo(() => {
    return filteredWatchlist.filter(item =>
      item.type === 'tv' &&
      item.status !== 'Returning Series' &&
      hasWatchProgress(item) &&
      !item.currentlyWatching &&
      !isComplete(item) &&
      !isWatching(item) &&
      !isRecentlyAdded(item) &&
      !(item.hasNewContent && item.status !== 'Ended' && item.status !== 'Cancelled')
    ).length;
  }, [filteredWatchlist]);

  // Priority 5: Unwatched (exclude all above; unstarted returning shows stay here)
  const unwatchedTVCount = useMemo(() => {
    return filteredWatchlist.filter(item =>
      item.type === 'tv' &&
      !hasWatchProgress(item) &&
      !item.currentlyWatching &&
      !isWatching(item) &&
      !isRecentlyAdded(item) &&
      !(item.hasNewContent && item.status !== 'Ended' && item.status !== 'Cancelled')
    ).length;
  }, [filteredWatchlist]);

  // Priority 6: Complete (exclude all above including Returning)
  const completeTVCount = useMemo(() => {
    return filteredWatchlist.filter(item =>
      isComplete(item) &&
      item.status !== 'Returning Series' &&
      !isWatching(item) &&
      !isRecentlyAdded(item) &&
      !(item.hasNewContent && item.status !== 'Ended' && item.status !== 'Cancelled')
    ).length;
  }, [filteredWatchlist]);

  const newlyAddedMoviesCount = useMemo(() => {
    return filteredWatchlist.filter(item => item.type === 'movie' && item.newlyAdded && !item.currentlyWatching).length;
  }, [filteredWatchlist]);

  const unwatchedMoviesCount = useMemo(() => {
    return filteredWatchlist.filter(item => item.type === 'movie' && !item.watched && !item.newlyAdded && !item.currentlyWatching).length;
  }, [filteredWatchlist]);

  const watchedMoviesCount = useMemo(() => {
    return filteredWatchlist.filter(item => item.type === 'movie' && item.watched).length;
  }, [filteredWatchlist]);

  // Cleanup observers on unmount
  useEffect(() => {
    return () => {
      observersRef.current.forEach((observer) => observer.disconnect());
      observersRef.current.clear();
    };
  }, []);
  // Also covers the moment between "signed out" and the redirect landing, so
  // the "Start Your Journey" empty state doesn't flash for signed-out visitors
  if (authLoading || !user) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-pulse text-sm text-white/50">Loading…</div>
      </div>
    );
  }

  const tabSwitcher = (
    <div role="tablist" aria-label="Dashboard sections" className="flex items-center gap-6 md:gap-8 max-w-full overflow-x-auto scrollbar-hide shadow-[inset_0_-1px_0_rgba(255,255,255,0.08)]">
      {([
        { key: 'watchlist', label: 'Watchlist' },
        { key: 'playlists', label: 'Playlists' },
        { key: 'together', label: 'Watch Together' },
      ] as const).map(({ key, label }) => (
        <button
          key={key}
          role="tab"
          aria-selected={dashboardTab === key}
          onClick={() => selectDashboardTab(key)}
          className={`relative h-11 shrink-0 text-sm md:text-[15px] font-medium whitespace-nowrap transition-colors focus:outline-none focus-visible:text-white ${
            dashboardTab === key ? 'text-white' : 'text-white/50 hover:text-white/80'
          }`}
        >
          {label}
          {dashboardTab === key && (
            <span aria-hidden className="absolute inset-x-0 bottom-0 h-0.5 rounded-full bg-red-600" />
          )}
        </button>
      ))}
    </div>
  );

  // Sections render nothing when search/filters exclude everything — say so
  const noWatchlistMatches =
    watchlist.length > 0 && currentlyWatchingCount === 0 && filteredWatchlist.length === 0;

  return (
    <div className="min-h-screen bg-black">
      <Header showSearch={true} transparent={true} page="dashboard" />

      <main>
        {watchlist.length === 0 && dashboardTab === 'watchlist' && !watchlistSynced ? (
          /* Watchlist still arriving from Firestore (fresh device) — don't
             flash the "Start Your Journey" empty state at existing users */
          <div className="px-gutter pt-20 md:pt-24 pb-8">
            <Skeleton className="h-10 w-48 mb-8 rounded-xl" />
            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 2xl:grid-cols-8 gap-x-3 md:gap-x-4 gap-y-6 md:gap-y-8">
              {Array.from({ length: 10 }).map((_, i) => (
                <Skeleton key={i} className="aspect-[2/3] rounded-xl" />
              ))}
            </div>
          </div>
        ) : watchlist.length === 0 && dashboardTab === 'watchlist' ? (
          /* Empty state */
          <div className="min-h-[80vh] flex items-center justify-center px-gutter pt-20 md:pt-24 pb-12">
            <div className="text-center space-y-8 max-w-xl mx-auto">
              <div className="space-y-3">
                <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-white">
                  Start Your Journey
                </h1>
                <p className="text-base md:text-lg text-white/60 leading-relaxed">
                  Discover and track your favorite shows and movies with personalized recommendations
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  type="button"
                  onClick={() => setShowSearchDialog(true)}
                  className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full bg-white text-black font-semibold hover:bg-white/90 transition-colors"
                >
                  <Plus className="h-5 w-5" />
                  Add Your First Show
                </button>
                <button
                  type="button"
                  onClick={() => router.push('/')}
                  className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full bg-white/10 ring-1 ring-white/15 text-white font-medium hover:bg-white/15 transition-colors"
                >
                  Browse Popular Content
                </button>
              </div>

              {/* Playlists / Watch Together stay reachable with an empty watchlist */}
              <div className="flex justify-center">{tabSwitcher}</div>
            </div>
          </div>
        ) : (
          <div className={watchlist.length > 0 ? undefined : 'pt-20 md:pt-24'}>
            {/* Cinematic Hero — featured watchlist title as backdrop */}
            {watchlist.length > 0 && (() => {
              // Prefer something with new content & a backdrop; else newest with a backdrop.
              const featured =
                watchlist.find(i => i.hasNewContent && i.backdropPath) ||
                watchlist.find(i => i.backdropPath) ||
                watchlist[0];
              const featuredTitle = featured?.name || 'Your Watchlist';
              const featuredHref = featured ? `/${featured.type}/${featured.id}` : '/';
              const watchedCount = watchlist.filter(i => i.watched || (i.watchedSeasons && i.watchedSeasons.length > 0)).length;
              return (
                <div className="relative min-h-[58vh] md:min-h-[64vh] overflow-hidden">
                  {/* Backdrop */}
                  <div className="absolute inset-0">
                    {featured?.backdropPath && (
                      <Image
                        src={tmdb.getImageUrl(featured.backdropPath, 'original')}
                        alt={featuredTitle}
                        fill
                        sizes="100vw"
                        className="object-cover object-top opacity-80"
                        priority
                      />
                    )}
                    {/* Legibility scrims + bottom fade into the page */}
                    <div className="absolute inset-0 bg-gradient-to-r from-black via-black/60 to-black/10" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/30" />
                    <div className="absolute bottom-0 inset-x-0 h-64 bg-gradient-to-t from-black via-black/90 to-transparent" />
                  </div>

                  {/* Content — bottom-aligned, clears navbar + PWA banner */}
                  <div
                    className="relative z-10 min-h-[58vh] md:min-h-[64vh] px-gutter flex flex-col justify-end pb-6 md:pb-10"
                    style={{ paddingTop: 'calc(7rem + var(--pwa-banner-height, 0px))' }}
                  >
                    <div className="max-w-3xl">
                      {/* Eyebrow + avatar/name */}
                      <div className="flex items-center gap-2.5 mb-3 md:mb-4">
                        <span className="h-8 w-8 rounded-full bg-white/10 flex items-center justify-center overflow-hidden flex-shrink-0 ring-1 ring-white/15">
                          {userProfile?.photoURL || user?.photoURL ? (
                            <Image src={userProfile?.photoURL || user?.photoURL || ''} alt="Profile" width={32} height={32} className="h-full w-full object-cover" />
                          ) : (
                            <span className="text-xs font-semibold text-white">
                              {(userProfile?.displayName || userProfile?.username || user?.email || 'U').charAt(0).toUpperCase()}
                            </span>
                          )}
                        </span>
                        <span className="eyebrow truncate">
                          Welcome back, {userProfile?.displayName || userProfile?.username || user?.email?.split('@')[0]}
                        </span>
                      </div>

                      <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.05] mb-3">
                        Your Watchlist
                      </h1>

                      {/* Featured tagline */}
                      {featured && (
                        <p className="text-sm md:text-base text-white/70 mb-3">
                          Continue with{' '}
                          <Link href={featuredHref} className="text-white font-semibold hover:underline underline-offset-4">
                            {featuredTitle}
                          </Link>
                          {featured.hasNewContent && <span className="ml-2 text-red-400">· New content</span>}
                        </p>
                      )}

                      {/* Stats — one quiet meta line */}
                      <p className="flex flex-wrap items-center gap-x-2 text-xs md:text-sm text-white/50 mb-5 md:mb-6">
                        <span>{watchlist.length} {watchlist.length === 1 ? 'title' : 'titles'}</span>
                        {watchedCount > 0 && (
                          <>
                            <span aria-hidden>·</span>
                            <span>{watchedCount} watched</span>
                          </>
                        )}
                        {newContentCount > 0 && (
                          <>
                            <span aria-hidden>·</span>
                            <span className="text-red-400 font-medium">{newContentCount} new</span>
                          </>
                        )}
                      </p>

                      {/* Actions */}
                      <div className="flex items-center gap-2 md:gap-3">
                        {featured && (
                          <Link
                            href={featuredHref}
                            className="flex-1 sm:flex-none flex items-center justify-center gap-2 h-11 md:h-12 px-6 md:px-7 bg-white hover:bg-white/90 text-black text-sm md:text-base font-semibold rounded-full transition-colors whitespace-nowrap"
                          >
                            <PlayCircle className="h-5 w-5" />
                            Continue
                          </Link>
                        )}
                        <button
                          onClick={() => setShowSearchDialog(true)}
                          className="flex-1 sm:flex-none flex items-center justify-center gap-2 h-11 md:h-12 px-6 md:px-7 bg-white/10 ring-1 ring-white/15 text-white hover:bg-white/15 backdrop-blur-md text-sm md:text-base font-semibold rounded-full transition-colors whitespace-nowrap"
                        >
                          <Plus className="h-5 w-5" />
                          Add Title
                        </button>
                        {/* Upcoming episodes + releases (icon-only on phones) */}
                        <Link
                          href="/calendar"
                          aria-label="Upcoming calendar"
                          className="flex-shrink-0 flex items-center justify-center gap-2 h-11 w-11 sm:w-auto md:h-12 sm:px-6 md:px-7 bg-white/10 ring-1 ring-white/15 text-white hover:bg-white/15 backdrop-blur-md text-sm md:text-base font-semibold rounded-full transition-colors whitespace-nowrap"
                        >
                          <Calendar className="h-5 w-5" />
                          <span className="hidden sm:inline">Upcoming</span>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Main Content */}
            <div className="px-gutter pt-4 pb-12 md:pt-6 md:pb-16 space-y-8 md:space-y-10">

              {/* Hub tab switcher — everything lives on this one page */}
              {tabSwitcher}

              {dashboardTab === 'playlists' && <PlaylistsPanel />}
              {dashboardTab === 'together' && <WatchTogetherPanel />}

              {dashboardTab === 'watchlist' && (<>

              {/* Watchlist search + sort + filter — single unified glass row */}
              {watchlist.length > 0 && (
                <div className="flex flex-row items-center gap-2">
                  {/* Search input */}
                  <div className="relative flex-1 min-w-0">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40 pointer-events-none" />
                    <Input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search your watchlist…"
                      aria-label="Search your watchlist"
                      className="w-full h-11 rounded-xl bg-white/[0.06] dark:bg-white/[0.06] ring-1 ring-white/10 border-0 shadow-none pl-11 pr-10 text-white placeholder:text-white/40 focus-visible:ring-1 focus-visible:ring-white/30 transition-shadow"
                    />
                    {searchQuery && (
                      <button
                        type="button"
                        onClick={() => setSearchQuery('')}
                        aria-label="Clear search"
                        className="absolute right-1.5 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-colors"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  {/* Mobile: single icon-only Filter & Sort pill */}
                  <button
                    type="button"
                    onClick={() => setShowFilterSortDialog(true)}
                    aria-label="Filter & Sort"
                    className="sm:hidden h-11 w-11 shrink-0 rounded-xl bg-white/[0.06] ring-1 ring-white/10 text-white hover:bg-white/10 transition-colors flex items-center justify-center focus:outline-none focus-visible:ring-white/30"
                  >
                    <Filter className="h-4 w-4" />
                  </button>

                  {/* Desktop: Sort + Hierarchical Filter pills */}
                  <div className="hidden sm:flex flex-row gap-2 shrink-0">
                    <Select
                      value={sortBy}
                      onValueChange={(value) => setSortBy(value as any)}
                      open={openDropdown === 'sort'}
                      onOpenChange={(open) => setOpenDropdown(open ? 'sort' : null)}
                    >
                      <SelectTrigger className="w-auto flex-shrink-0 h-11 rounded-xl bg-white/[0.06] ring-1 ring-white/10 border-0 px-4 text-white hover:bg-white/10 transition-colors font-medium text-sm gap-2 focus:outline-none focus:ring-1 focus:ring-white/30 focus:ring-offset-0">
                        <ArrowUpDown className="h-4 w-4 text-white/50" />
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent
                        className="bg-zinc-950 border-white/10 rounded-xl p-1 w-[280px] max-h-[60vh] overflow-y-auto"
                        sideOffset={8}
                        align="start"
                        side="bottom"
                        position="popper"
                      >
                        <SelectItem value="recently-updated" className="text-white rounded-lg py-2 hover:bg-white/[0.06] focus:bg-white/[0.06]">
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-white/40" />
                            Recently Updated
                          </div>
                        </SelectItem>
                        <SelectItem value="recently-added" className="text-white rounded-lg py-2 hover:bg-white/[0.06] focus:bg-white/[0.06]">
                          <div className="flex items-center gap-2">
                            <Plus className="h-4 w-4 text-white/40" />
                            Recently Added
                          </div>
                        </SelectItem>
                        <SelectItem value="name-asc" className="text-white rounded-lg py-2 hover:bg-white/[0.06] focus:bg-white/[0.06]">
                          <div className="flex items-center gap-2">
                            <ArrowRight className="h-4 w-4 text-white/40 rotate-[-90deg]" />
                            Name (A-Z)
                          </div>
                        </SelectItem>
                        <SelectItem value="name-desc" className="text-white rounded-lg py-2 hover:bg-white/[0.06] focus:bg-white/[0.06]">
                          <div className="flex items-center gap-2">
                            <ArrowRight className="h-4 w-4 text-white/40 rotate-90" />
                            Name (Z-A)
                          </div>
                        </SelectItem>
                        <SelectItem value="rating-high" className="text-white rounded-lg py-2 hover:bg-white/[0.06] focus:bg-white/[0.06]">
                          <div className="flex items-center gap-2">
                            <Star className="h-4 w-4 text-white/40" />
                            Rating (High-Low)
                          </div>
                        </SelectItem>
                        <SelectItem value="rating-low" className="text-white rounded-lg py-2 hover:bg-white/[0.06] focus:bg-white/[0.06]">
                          <div className="flex items-center gap-2">
                            <Star className="h-4 w-4 text-white/40" />
                            Rating (Low-High)
                          </div>
                        </SelectItem>
                        <SelectItem value="seasons-most" className="text-white rounded-lg py-2 hover:bg-white/[0.06] focus:bg-white/[0.06]">
                          <div className="flex items-center gap-2">
                            <Tv className="h-4 w-4 text-white/40" />
                            Seasons (Most)
                          </div>
                        </SelectItem>
                        <SelectItem value="seasons-least" className="text-white rounded-lg py-2 hover:bg-white/[0.06] focus:bg-white/[0.06]">
                          <div className="flex items-center gap-2">
                            <Tv className="h-4 w-4 text-white/40" />
                            Seasons (Least)
                          </div>
                        </SelectItem>
                        <SelectItem value="personal-rank" className="text-white rounded-lg py-2 hover:bg-white/[0.06] focus:bg-white/[0.06]">
                          <div className="flex items-center gap-2">
                            <TrendingUp className="h-4 w-4 text-white/40" />
                            Personal Rank
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>

                    <HierarchicalFilter
                      open={openDropdown === 'filter'}
                      onOpenChange={(open) => setOpenDropdown(open ? 'filter' : null)}
                    />
                  </div>
                </div>
              )}

              {/* Currently Watching — TV + Movies combined, the user's active pool */}
              {currentlyWatchingCount > 0 && (
                <CollapsibleSection
                  sectionKey="currentlyWatching"
                  title="Currently Watching"
                  icon={PlayCircle}
                  count={currentlyWatchingCount}
                  countLabel={{ singular: 'title', plural: 'titles' }}
                >
                  <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 2xl:grid-cols-8 gap-x-3 md:gap-x-4 gap-y-6 md:gap-y-8">
                    {currentlyWatchingItems
                      .slice(0, itemsToShow.currentlyWatching)
                      .map((item, index) => (
                        <WatchlistCard
                          key={`${item.type}-${item.id}`}
                          item={item}
                          showQuickComplete={true}
                          priority={index < 6}
                          highlighted={item.id === highlightedItemId}
                          onOpenDialog={handleOpenItemDialog}
                        />
                      ))}
                  </div>
                  {currentlyWatchingCount > itemsToShow.currentlyWatching && (
                    <div ref={currentlyWatchingObserverRef} className="h-20 flex items-center justify-center text-white/40 text-sm">
                      Loading more...
                    </div>
                  )}
                </CollapsibleSection>
              )}

              {/* TV Shows: Recently Added Section */}
              {recentlyAddedTVCount > 0 && (activeFilters.length === 0 ? (filterType === 'all' || filterType === 'tv') : activeFilters.includes('tv-recently-added')) && (
                <CollapsibleSection
                  sectionKey="tv-recently-added"
                  title="TV Shows: Recently Added"
                  icon={Sparkles}
                  count={recentlyAddedTVCount}
                  countLabel={{ singular: 'show', plural: 'shows' }}
                >
                  <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 2xl:grid-cols-8 gap-x-3 md:gap-x-4 gap-y-6 md:gap-y-8">
                    {filteredWatchlist
                      .filter(item => item.type === 'tv' && isRecentlyAdded(item) && !isWatching(item))
                      .slice(0, itemsToShow.recentlyAddedTV)
                      .map((item, index) => (
                        <WatchlistCard key={item.id} item={item} priority={index < 6} highlighted={item.id === highlightedItemId} onOpenDialog={handleOpenItemDialog} />
                      ))}
                  </div>
                  {recentlyAddedTVCount > itemsToShow.recentlyAddedTV && (
                    <div ref={recentlyAddedTVObserverRef} className="h-20 flex items-center justify-center text-white/40 text-sm">
                      Loading more...
                    </div>
                  )}
                </CollapsibleSection>
              )}

              {/* TV Shows: New Content Section */}
              {newContentTVCount > 0 && (activeFilters.length === 0 ? (filterType === 'all' || filterType === 'tv' || filterType === 'new') : activeFilters.includes('tv-new')) && (
                <CollapsibleSection
                  sectionKey="tv-new"
                  title="TV Shows: New Content"
                  icon={Bell}
                  count={newContentTVCount}
                  countLabel={{ singular: 'show', plural: 'shows' }}
                >
                  <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 2xl:grid-cols-8 gap-x-3 md:gap-x-4 gap-y-6 md:gap-y-8">
                    {filteredWatchlist
                      .filter(item =>
                        item.type === 'tv' &&
                        item.hasNewContent &&
                        item.status !== 'Ended' &&
                        item.status !== 'Cancelled' &&
                        !isWatching(item) &&
                        !isRecentlyAdded(item)
                      )
                      .slice(0, itemsToShow.newContentTV)
                      .map((item, index) => (
                        <WatchlistCard key={item.id} item={item} priority={index < 6} highlighted={item.id === highlightedItemId} onOpenDialog={handleOpenItemDialog} />
                      ))}
                  </div>
                  {newContentTVCount > itemsToShow.newContentTV && (
                    <div ref={newContentTVObserverRef} className="h-20 flex items-center justify-center text-white/40 text-sm">
                      Loading more...
                    </div>
                  )}
                </CollapsibleSection>
              )}

              {/* TV Shows: In Progress — auto-detected progress (episodes watched but not marked as actively watching) */}
              {inProgressTVCount > 0 && (activeFilters.length === 0 ? (filterType === 'all' || filterType === 'tv' || filterType === 'new') : activeFilters.includes('tv-in-progress')) && (
                <CollapsibleSection
                  sectionKey="tv-in-progress"
                  title="TV Shows: In Progress"
                  icon={Tv}
                  count={inProgressTVCount}
                  countLabel={{ singular: 'show', plural: 'shows' }}
                >
                  <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 2xl:grid-cols-8 gap-x-3 md:gap-x-4 gap-y-6 md:gap-y-8">
                    {filteredWatchlist
                      .filter(item =>
                        item.type === 'tv' &&
                        item.status !== 'Returning Series' &&
                        hasWatchProgress(item) &&
                        !item.currentlyWatching &&
                        !isComplete(item) &&
                        !isWatching(item) &&
                        !isRecentlyAdded(item) &&
                        !(item.hasNewContent && item.status !== 'Ended' && item.status !== 'Cancelled')
                      )
                      .filter(item => {
                        if (filterType === 'new') return item.hasNewContent;
                        return true;
                      })
                      .slice(0, itemsToShow.inProgressTV)
                      .map((item, index) => (
                        <WatchlistCard key={item.id} item={item} priority={index < 6} highlighted={item.id === highlightedItemId} onOpenDialog={handleOpenItemDialog} />
                      ))}
                  </div>
                  {inProgressTVCount > itemsToShow.inProgressTV && (
                    <div ref={inProgressTVObserverRef} className="h-20 flex items-center justify-center text-white/40 text-sm">
                      Loading more...
                    </div>
                  )}
                </CollapsibleSection>
              )}

              {/* TV Shows: Returning — every show coming back with more episodes,
                  sorted by soonest upcoming episode */}
              {returningTVCount > 0 && (activeFilters.length === 0 ? (filterType === 'all' || filterType === 'tv') : activeFilters.includes('tv-returning')) && (
                <CollapsibleSection
                  sectionKey="tv-returning"
                  title="TV Shows: Returning"
                  icon={RefreshCw}
                  count={returningTVCount}
                  countLabel={{ singular: 'show', plural: 'shows' }}
                >
                  <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 2xl:grid-cols-8 gap-x-3 md:gap-x-4 gap-y-6 md:gap-y-8">
                    {filteredWatchlist
                      .filter(isReturningSectionItem)
                      .sort((a, b) => {
                        // Shows with a known upcoming episode first, soonest first
                        const aDate = a.nextEpisode?.airDate ? new Date(a.nextEpisode.airDate).getTime() : Infinity;
                        const bDate = b.nextEpisode?.airDate ? new Date(b.nextEpisode.airDate).getTime() : Infinity;
                        if (aDate !== bDate) return aDate - bDate;
                        return a.name.localeCompare(b.name);
                      })
                      .slice(0, itemsToShow.returningTV)
                      .map((item, index) => (
                        <WatchlistCard key={item.id} item={item} priority={index < 6} highlighted={item.id === highlightedItemId} onOpenDialog={handleOpenItemDialog} />
                      ))}
                  </div>
                  {returningTVCount > itemsToShow.returningTV && (
                    <div ref={returningTVObserverRef} className="h-20 flex items-center justify-center text-white/40 text-sm">
                      Loading more...
                    </div>
                  )}
                </CollapsibleSection>
              )}

              {/* TV Shows: Unwatched — never started */}
              {unwatchedTVCount > 0 && (activeFilters.length === 0 ? (filterType === 'all' || filterType === 'tv' || filterType === 'new') : activeFilters.includes('tv-unwatched')) && (
                <CollapsibleSection
                  sectionKey="tv-unwatched"
                  title="TV Shows: Unwatched"
                  icon={PlayCircle}
                  iconTone="neutral"
                  count={unwatchedTVCount}
                  countLabel={{ singular: 'show', plural: 'shows' }}
                >
                  <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 2xl:grid-cols-8 gap-x-3 md:gap-x-4 gap-y-6 md:gap-y-8">
                    {filteredWatchlist
                      .filter(item =>
                        item.type === 'tv' &&
                        !hasWatchProgress(item) &&
                        !item.currentlyWatching &&
                        !isWatching(item) &&
                        !isRecentlyAdded(item) &&
                        !(item.hasNewContent && item.status !== 'Ended' && item.status !== 'Cancelled')
                      )
                      .slice(0, itemsToShow.unwatchedTV)
                      .map((item, index) => (
                        <WatchlistCard key={item.id} item={item} priority={index < 6} highlighted={item.id === highlightedItemId} onOpenDialog={handleOpenItemDialog} />
                      ))}
                  </div>
                  {unwatchedTVCount > itemsToShow.unwatchedTV && (
                    <div ref={unwatchedTVObserverRef} className="h-20 flex items-center justify-center text-white/40 text-sm">
                      Loading more...
                    </div>
                  )}
                </CollapsibleSection>
              )}

              {/* TV Shows: Complete */}
              {completeTVCount > 0 && (activeFilters.length === 0 ? (filterType === 'all' || filterType === 'tv') : activeFilters.includes('tv-complete')) && (
                <CollapsibleSection
                  sectionKey="tv-complete"
                  title="TV Shows: Complete"
                  icon={Tv}
                  iconTone="neutral"
                  count={completeTVCount}
                  countLabel={{ singular: 'show', plural: 'shows' }}
                >
                  <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 2xl:grid-cols-8 gap-x-3 md:gap-x-4 gap-y-6 md:gap-y-8">
                    {filteredWatchlist
                      .filter(item =>
                        isComplete(item) &&
                        item.status !== 'Returning Series' &&
                        !isWatching(item) &&
                        !isRecentlyAdded(item) &&
                        !(item.hasNewContent && item.status !== 'Ended' && item.status !== 'Cancelled')
                      )
                      .slice(0, itemsToShow.completeTV)
                      .map((item, index) => (
                        <WatchlistCard key={item.id} item={item} priority={index < 6} highlighted={item.id === highlightedItemId} onOpenDialog={handleOpenItemDialog} />
                      ))}
                  </div>
                  {completeTVCount > itemsToShow.completeTV && (
                    <div ref={completeTVObserverRef} className="h-20 flex items-center justify-center text-white/40 text-sm">
                      Loading more...
                    </div>
                  )}
                </CollapsibleSection>
              )}

              {/* Movies: Recently Added (temporary holding bucket — moves to Unwatched on refresh) */}
              {newlyAddedMoviesCount > 0 && (activeFilters.length === 0 ? (filterType === 'all' || filterType === 'movie') : activeFilters.includes('movie-recently-added')) && (
                <CollapsibleSection
                  sectionKey="movie-recently-added"
                  title="Movies: Recently Added"
                  icon={Sparkles}
                  count={newlyAddedMoviesCount}
                  countLabel={{ singular: 'movie', plural: 'movies' }}
                  rightSlot={
                    <span className="hidden md:inline text-xs text-white/45">
                      Mark as watched or these will move to Unwatched on refresh
                    </span>
                  }
                >
                  <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 2xl:grid-cols-8 gap-x-3 md:gap-x-4 gap-y-6 md:gap-y-8">
                    {filteredWatchlist
                      .filter(item => item.type === 'movie' && item.newlyAdded && !item.currentlyWatching)
                      .slice(0, itemsToShow.newlyAddedMovies)
                      .map((item, index) => (
                        <WatchlistCard key={item.id} item={item} priority={index < 6} highlighted={item.id === highlightedItemId} onOpenDialog={handleOpenItemDialog} />
                      ))}
                  </div>
                  {newlyAddedMoviesCount > itemsToShow.newlyAddedMovies && (
                    <div ref={newlyAddedMoviesObserverRef} className="h-20 flex items-center justify-center text-white/40 text-sm">
                      Loading more...
                    </div>
                  )}
                </CollapsibleSection>
              )}

              {/* Movies: Unwatched */}
              {unwatchedMoviesCount > 0 && (activeFilters.length === 0 ? (filterType === 'all' || filterType === 'movie') : activeFilters.includes('movie-unwatched')) && (
                <CollapsibleSection
                  sectionKey="movie-unwatched"
                  title="Movies: Unwatched"
                  icon={Film}
                  iconTone="neutral"
                  count={unwatchedMoviesCount}
                  countLabel={{ singular: 'movie', plural: 'movies' }}
                >
                  <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 2xl:grid-cols-8 gap-x-3 md:gap-x-4 gap-y-6 md:gap-y-8">
                    {filteredWatchlist
                      .filter(item => item.type === 'movie' && !item.watched && !item.newlyAdded && !item.currentlyWatching)
                      .slice(0, itemsToShow.unwatchedMovies)
                      .map((item, index) => (
                        <WatchlistCard key={item.id} item={item} priority={index < 6} highlighted={item.id === highlightedItemId} onOpenDialog={handleOpenItemDialog} />
                      ))}
                  </div>
                  {unwatchedMoviesCount > itemsToShow.unwatchedMovies && (
                    <div ref={unwatchedMoviesObserverRef} className="h-20 flex items-center justify-center text-white/40 text-sm">
                      Loading more...
                    </div>
                  )}
                </CollapsibleSection>
              )}

              {/* Movies: Complete */}
              {watchedMoviesCount > 0 && (activeFilters.length === 0 ? (filterType === 'all' || filterType === 'movie') : activeFilters.includes('movie-complete')) && (
                <CollapsibleSection
                  sectionKey="movie-complete"
                  title="Movies: Complete"
                  icon={Film}
                  count={watchedMoviesCount}
                  countLabel={{ singular: 'movie', plural: 'movies' }}
                >
                  <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 2xl:grid-cols-8 gap-x-3 md:gap-x-4 gap-y-6 md:gap-y-8">
                    {filteredWatchlist
                      .filter(item => item.type === 'movie' && item.watched)
                      .slice(0, itemsToShow.watchedMovies)
                      .map((item, index) => (
                        <WatchlistCard key={item.id} item={item} priority={index < 6} highlighted={item.id === highlightedItemId} onOpenDialog={handleOpenItemDialog} />
                      ))}
                  </div>
                  {watchedMoviesCount > itemsToShow.watchedMovies && (
                    <div ref={watchedMoviesObserverRef} className="h-20 flex items-center justify-center text-white/40 text-sm">
                      Loading more...
                    </div>
                  )}
                </CollapsibleSection>
              )}

              {/* Search / filters matched nothing */}
              {noWatchlistMatches && (
                <div className="text-center py-16 space-y-4">
                  <p className="text-base md:text-lg text-white/70">
                    {searchQuery ? <>Nothing on your watchlist matches &ldquo;{searchQuery}&rdquo;</> : 'No titles match these filters'}
                  </p>
                  <div className="flex flex-wrap justify-center gap-2">
                    {searchQuery && (
                      <Button variant="outline" onClick={() => setSearchQuery('')} className="h-10 rounded-full border-0 ring-1 ring-white/15 bg-white/10 text-white hover:bg-white/15 hover:text-white">
                        Clear search
                      </Button>
                    )}
                    {activeFilters.length > 0 && (
                      <Button variant="outline" onClick={clearAllFilters} className="h-10 rounded-full border-0 ring-1 ring-white/15 bg-white/10 text-white hover:bg-white/15 hover:text-white">
                        Clear filters
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Empty State for Filtered Results */}
              {((filterType === 'tv' && watchlist.filter(item => item.type === 'tv').length === 0) ||
                (filterType === 'movie' && watchlist.filter(item => item.type === 'movie').length === 0) ||
                (filterType === 'new' && newContentCount === 0)) && (
                <div className="text-center py-16 space-y-6">
                  <div className="space-y-2">
                    <p className="text-lg text-white/80">No {filterType === 'tv' ? 'TV shows' : filterType === 'movie' ? 'movies' : 'new content'} found</p>
                    <p className="text-sm text-white/50">Add some content to your watchlist to start tracking</p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setShowSearchDialog(true)}
                    className="h-10 rounded-full border-0 ring-1 ring-white/15 bg-white/10 text-white hover:bg-white/15 hover:text-white"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add Content
                  </Button>
                </div>
              )}

              </>)}
            </div>
          </div>
        )}

        <SearchDialog
          isOpen={showSearchDialog}
          onClose={() => setShowSearchDialog(false)}
        />

        {/* Mobile Filter & Sort Dialog */}
        <FilterSortDialog
          open={showFilterSortDialog}
          onOpenChange={setShowFilterSortDialog}
        />
      </main>

      {/* Notification Permission Prompt */}
      <NotificationPermissionPrompt />

      {/* Item Detail Dialog */}
      <ItemDetailDialog
        isOpen={isDetailDialogOpen}
        onClose={closeDetailDialog}
        itemId={currentItemId}
        itemType={currentItemType}
        onNavigateNext={navigateNext}
        onNavigatePrev={navigatePrev}
        canNavigateNext={canNavigateNext}
        canNavigatePrev={canNavigatePrev}
        watchlist={watchlist}
      />
    </div>
  );
}